import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import t from './tokens';

// Embeds the Printess web-to-print editor for a personalized card/gift via the
// editor.printess.com iframe (embed.html). The editor runs INSIDE that iframe,
// same-origin to Printess — importing its 4.7MB ES module into our own page
// instead fails (Printess's /templates/load + /user/settings APIs block
// cross-origin CORS), so the iframe is the only workable embed.
//
// embed.html exposes a generic bridge in its postMessage handler: any message
// `{ cmd: "<apiMethod>", parameters: [...] }` runs printess[apiMethod](...) inside
// the iframe. We use it to call `importImageFromUrl` so the student's Shoob photos
// become selectable images in the editor: the pre-selected pose is placed into the
// photo frame (by id, see below), the rest land in the image list so the customer
// can swap.
// "Add to Basket" posts back a save token + thumbnail we put on the cart. Without
// a PRINTESS_SHOP_TOKEN the editor loads Printess's demo mode.
const EDITOR_ORIGIN = 'https://editor.printess.com';
const EDITOR_SRC = `${EDITOR_ORIGIN}/printess-editor/embed.html`;
// The template's photo frame carries this CSS class so we can drop the customer's
// photo into exactly that frame (not a fuzzy "next possible/new" frame). Every
// Printess product template's photo frame must have Style [Class] = this value.
const PHOTO_FRAME_CLASS = 'photoframe';
// Safety net: if the pre-selected pose's import reply never arrives, import the
// alternates anyway after this long so the customer can still swap photos.
const IMPORT_REST_FALLBACK_MS = 8000;

const s = {
  page: {
    position: 'fixed', inset: 0, zIndex: 1100, background: '#111827', display: 'flex', flexDirection: 'column',
  },
  bar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '1rem',
    padding: '0.6rem 1rem',
    background: t.surface,
    borderBottom: `1px solid ${t.border}`,
  },
  back: {
    background: 'none', border: 'none', color: t.brand, fontWeight: 700, cursor: 'pointer', fontSize: '0.95rem',
  },
  title: { fontWeight: 700, color: t.ink },
  note: { color: t.muted, fontSize: '0.8rem' },
  barRight: { display: 'flex', alignItems: 'center', gap: '0.75rem' },
  cta: {
    background: t.brand,
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '0.5rem 1.1rem',
    fontWeight: 700,
    cursor: 'pointer',
    fontSize: '0.9rem',
    whiteSpace: 'nowrap',
  },
  ctaOff: { opacity: 0.6, cursor: 'default' },
  frameWrap: { position: 'relative', flex: 1, background: '#1f2937' },
  frame: {
    width: '100%', height: '100%', border: 'none', display: 'block',
  },
  busy: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    gap: '0.6rem',
    pointerEvents: 'none',
  },
};

const PrintessEditor = ({
  token, product, photoUrl, photos, resumeToken, adding, onBasket, onBack,
}) => {
  const frameRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);
  // Read inside the (load-keyed) message listener without re-binding it.
  const resumeRef = useRef(resumeToken);
  resumeRef.current = resumeToken;
  const placedRef = useRef(false); // have we placed the first photo into the frame yet?

  // The watermarked pose URLs to make available in the editor, pre-selected pose
  // FIRST — that ordering is load-bearing: index 0 is imported alone and placed into
  // the photo frame, the rest are imported afterwards as swap options.
  const photoUrls = useMemo(() => {
    const urls = (photos || []).map((p) => p && p.watermark_url).filter(Boolean);
    const ordered = [...urls];
    if (photoUrl) {
      const i = ordered.indexOf(photoUrl);
      if (i > 0) { ordered.splice(i, 1); ordered.unshift(photoUrl); } else if (i < 0) ordered.unshift(photoUrl);
    }
    return Array.from(new Set(ordered));
  }, [photos, photoUrl]);
  // Read the live photo list inside the (load-keyed) listener without re-binding.
  const photoUrlsRef = useRef(photoUrls);
  photoUrlsRef.current = photoUrls;

  // Make poses available in the editor's image list (so the customer can SWAP),
  // WITHOUT auto-placing any of them. We never use importImageFromUrl's auto-assign
  // (true) because it drops the photo into a NEW full-size frame rather than the
  // template's sized frame — that's what made the photo span the whole mug. Instead
  // we assign BY ID to the frame carrying PHOTO_FRAME_CLASS (message listener below).
  const importPhotos = (frame, urls) => {
    urls.forEach((url) => {
      frame.contentWindow.postMessage(
        { cmd: 'importImageFromUrl', parameters: [url, false] },
        EDITOR_ORIGIN,
      );
    });
  };

  // Is this import reply the pre-selected pose? Only answerable when the bridge
  // echoes the source url back (it isn't documented to, hence the sequencing below);
  // an unidentifiable reply is treated as NOT the pre-selected pose, so it can never
  // be placed on a guess.
  // TODO: see #4801 — confirm which of these fields the bridge actually populates.
  const isPreselected = (img) => {
    const url = img.url || img.originalUrl || img.src;
    return Boolean(url) && url === photoUrlsRef.current[0];
  };

  // The pre-selected pose is imported ALONE first, so the first import reply is
  // guaranteed to be that photo — each import is an async fetch inside the iframe,
  // so firing them all at once would let a faster-loading alternate pose win the
  // race and land in the frame instead. The rest are imported once the pre-selected
  // one has been placed (see the listener below). If that reply never arrives, this
  // timer still imports the alternates so the customer keeps the ability to swap —
  // after which placement requires positively identifying the pre-selected reply
  // (`isPreselected`), because several imports are then in flight at once.
  const importedRestRef = useRef(false);
  const restTimerRef = useRef(null);
  const importRest = (frame) => {
    if (importedRestRef.current || !frame) return;
    importedRestRef.current = true;
    importPhotos(frame, photoUrlsRef.current.slice(1));
  };

  // Add-to-cart via the api bridge instead of Printess's built-in basket button.
  // Printess's button runs print-resolution validation and HARD-BLOCKS our
  // intentionally low-res watermarked placeholder ("image too small, you cannot
  // continue") — a false alarm, since the full-res image is swapped in at
  // production. saveAndGenerateBasketThumbnailUrl just saves + renders a thumbnail
  // (no validation) and returns { saveToken, basketUrl } — the same payload the
  // native button would, which we forward to the cart on the bridge reply below.
  const addToCart = () => {
    const frame = frameRef.current;
    if (!frame || saving || adding) return;
    setSaving(true);
    frame.contentWindow.postMessage(
      { cmd: 'saveAndGenerateBasketThumbnailUrl', parameters: [400, 400] },
      EDITOR_ORIGIN,
    );
  };

  // attach config -> Printess. The pre-selected pose is placed via
  // `assingImageByIdToFramesWithClass` on its import reply (see the message listener
  // below) into the template's existing frame carrying PHOTO_FRAME_CLASS.
  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return undefined;
    placedRef.current = false; // fresh editor: photo not placed yet
    importedRestRef.current = false; // …and the alternates aren't imported yet
    const onLoad = () => {
      frame.contentWindow.postMessage({
        cmd: 'attach',
        properties: {
          token: token || '',
          // A Printess save token (st:…) passed as templateName RESUMES that saved
          // design (re-edit from the cart); otherwise load the template fresh.
          templateName: resumeToken || product.template_id || '',
          basketId: `senior_${product.id}_${Date.now()}`,
          shopUserId: 'senior_shop',
          // Hide Printess's own (resolution-validated) basket button; customers add
          // to cart via our bridge-driven button, which skips the false low-res block.
          showAddToBasketButton: false,
        },
      }, EDITOR_ORIGIN);
    };
    frame.addEventListener('load', onLoad);
    // Start the load only AFTER the listener is attached. If src were set in the
    // JSX, a cached embed.html can fire `load` before this effect runs, the event
    // is missed, the attach is never sent, and the editor spins forever.
    if (frame.getAttribute('src') !== EDITOR_SRC) frame.src = EDITOR_SRC;
    return () => frame.removeEventListener('load', onLoad);
  }, [token, product.id, product.template_id, resumeToken]);

  // Editor -> us: "loaded" = ready (import the photos); the bridge reply to our
  // saveAndGenerateBasketThumbnailUrl carries { saveToken, basketUrl } -> cart;
  // "basket" (native button, if ever shown) and "back" handled too.
  useEffect(() => {
    const onMessage = (event) => {
      if (event.origin !== EDITOR_ORIGIN) return;
      const data = event.data || {};
      if (data.cmd === 'loaded') {
        setReady(true);
        const frame = frameRef.current;
        if (!frame) return;
        if (resumeRef.current) {
          // Re-edit: the saved design already carries the customer's photo and
          // framing, so nothing gets placed and import order doesn't matter.
          importedRestRef.current = true;
          importPhotos(frame, photoUrlsRef.current);
        } else {
          // Fresh: pre-selected pose alone, alternates after it's placed.
          importPhotos(frame, photoUrlsRef.current.slice(0, 1));
          restTimerRef.current = setTimeout(() => importRest(frameRef.current), IMPORT_REST_FALLBACK_MS);
        }
      } else if (data.cmd === 'importImageFromUrl') {
        // The pre-selected photo finished importing -> assign it, by id, into the
        // template's frame that carries PHOTO_FRAME_CLASS. This targets the exact
        // (sized) frame rather than the fuzzy "next possible/new frame", so the photo
        // can't blow up to fill the whole document. Skip on resume (the saved design
        // already has it). Only the pre-selected pose is in flight at this point —
        // the alternates are imported right after, as swap options.
        const img = data.result;
        if (!resumeRef.current && !placedRef.current && img && img.id && frameRef.current) {
          // Once the fallback timer has fired, the alternates are in flight ALONGSIDE
          // the still-outstanding pre-selected import, so arrival order no longer
          // identifies the pre-selected pose. Place a reply then only if the bridge
          // echoes a url we can match against it. If it doesn't, place NOTHING: an
          // empty frame is visible and the customer can drag their pose in from the
          // image list, whereas silently framing the wrong photo would ship as-is.
          if (importedRestRef.current && !isPreselected(img)) return;
          placedRef.current = true;
          frameRef.current.contentWindow.postMessage(
            { cmd: 'assingImageByIdToFramesWithClass', parameters: [img.id, PHOTO_FRAME_CLASS, 'document'] },
            EDITOR_ORIGIN,
          );
          clearTimeout(restTimerRef.current);
          importRest(frameRef.current);
        }
      } else if (data.cmd === 'saveAndGenerateBasketThumbnailUrl' && data.result && data.result.saveToken) {
        setSaving(false); // hand off to the parent's `adding` state for the POST
        onBasket(data.result.saveToken, data.result.basketUrl);
      } else if (data.cmd === 'basket' && data.token) {
        onBasket(data.token, data.thumbnailUrl);
      } else if (data.cmd === 'back') {
        onBack();
      }
    };
    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
      clearTimeout(restTimerRef.current);
    };
  }, [onBasket, onBack]);

  return (
    <div style={s.page}>
      <div style={s.bar}>
        <button type="button" style={s.back} onClick={onBack}>← Back</button>
        <span style={s.title}>{`Personalize your ${product.name}`}</span>
        <div style={s.barRight}>
          {!token && <span style={s.note}>Demo mode</span>}
          <button
            type="button"
            style={{ ...s.cta, ...((!ready || saving || adding) ? s.ctaOff : {}) }}
            onClick={addToCart}
            disabled={!ready || saving || adding}
          >
            {saving || adding ? 'Adding…' : 'Add to cart'}
          </button>
        </div>
      </div>
      <div style={s.frameWrap}>
        <iframe
          ref={frameRef}
          title="Printess editor"
          style={s.frame}
          allow="clipboard-read; clipboard-write"
        />
        {(!ready || adding || saving) && (
          <div style={s.busy}>
            <span className="sg-lb-spinner" />
            <span>{(adding || saving) ? 'Adding to cart…' : 'Loading editor…'}</span>
          </div>
        )}
      </div>
    </div>
  );
};

PrintessEditor.propTypes = {
  token: PropTypes.string,
  product: PropTypes.shape({
    id: PropTypes.number, name: PropTypes.string, template_id: PropTypes.string,
  }).isRequired,
  photoUrl: PropTypes.string,
  // The student's ready poses (watermarked) the customer can choose/swap among.
  photos: PropTypes.arrayOf(PropTypes.shape({ watermark_url: PropTypes.string })),
  // A Printess save token (st:…) to resume a saved design for re-editing from the cart.
  resumeToken: PropTypes.string,
  adding: PropTypes.bool,
  onBasket: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

PrintessEditor.defaultProps = {
  token: '', photoUrl: null, photos: [], resumeToken: null, adding: false,
};

export default PrintessEditor;
